/**
 * Async Error Logger - S3 via Lambda
 *
 * Key principles:
 * - 100% non-blocking (fire and forget)
 * - Never throws, never affects app performance
 * - Batches logs to reduce API calls
 * - Uses sendBeacon for page unload
 */

interface ErrorLog {
  timestamp: string
  level: 'error' | 'warn' | 'info'
  errorType: string
  message: string
  technicalDetails?: string
  env: string // 'development' | 'staging' | 'production'
  context: {
    sessionId: string
    userId?: string
    page: string
    userAgent: string
  }
}

// Config - environment-aware
// Map Vite modes to log environments
function getEnv(): string {
  const mode = import.meta.env.MODE
  if (mode === 'production') return 'prod'
  if (mode === 'staging') return 'staging'
  return 'dev' // development or any other mode -> dev
}
const ENV = getEnv()
const LOG_ENDPOINT = import.meta.env.PROD
  ? 'https://8plbeu8soh.execute-api.us-east-1.amazonaws.com/prod/api/logs'
  : '/api/logs'
const BATCH_SIZE = 10
const BATCH_INTERVAL_MS = 30000 // 30 seconds
const MAX_RETRIES = 1

// State
let logBuffer: ErrorLog[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null
let sessionId: string = generateSessionId()

function generateSessionId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
}

/**
 * Get current session ID
 */
export function getSessionId(): string {
  return sessionId
}

/**
 * Log an error - completely non-blocking
 */
export function logError(
  errorType: string,
  message: string,
  technicalDetails?: string
): void {
  // Fire and forget - never block
  try {
    const log: ErrorLog = {
      timestamp: new Date().toISOString(),
      level: 'error',
      errorType,
      message,
      technicalDetails,
      env: ENV,
      context: {
        sessionId,
        userId: getUserId(),
        page: window.location.pathname,
        userAgent: navigator.userAgent,
      },
    }

    logBuffer.push(log)

    // Flush if buffer is full
    if (logBuffer.length >= BATCH_SIZE) {
      flushLogs()
    } else if (!flushTimer) {
      // Schedule flush
      flushTimer = setTimeout(flushLogs, BATCH_INTERVAL_MS)
    }
  } catch {
    // Silent fail - never affect app
  }
}

/**
 * Log a warning
 */
export function logWarning(
  errorType: string,
  message: string,
  technicalDetails?: string
): void {
  try {
    const log: ErrorLog = {
      timestamp: new Date().toISOString(),
      level: 'warn',
      errorType,
      message,
      technicalDetails,
      env: ENV,
      context: {
        sessionId,
        userId: getUserId(),
        page: window.location.pathname,
        userAgent: navigator.userAgent,
      },
    }

    logBuffer.push(log)

    if (logBuffer.length >= BATCH_SIZE) {
      flushLogs()
    } else if (!flushTimer) {
      flushTimer = setTimeout(flushLogs, BATCH_INTERVAL_MS)
    }
  } catch {
    // Silent fail
  }
}

/**
 * Flush logs to server - non-blocking
 */
function flushLogs(): void {
  if (flushTimer) {
    clearTimeout(flushTimer)
    flushTimer = null
  }

  if (logBuffer.length === 0) return

  const logsToSend = [...logBuffer]
  logBuffer = []

  // Fire and forget - don't await
  sendLogs(logsToSend)
}

/**
 * Send logs to Lambda - with retry
 */
async function sendLogs(logs: ErrorLog[], retryCount = 0): Promise<void> {
  try {
    const response = await fetch(LOG_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ logs }),
      // Short timeout to not block
      signal: AbortSignal.timeout(5000),
    })

    if (!response.ok && retryCount < MAX_RETRIES) {
      // Retry once
      setTimeout(() => sendLogs(logs, retryCount + 1), 1000)
    }
  } catch {
    // Silent fail - retry once if first attempt
    if (retryCount < MAX_RETRIES) {
      setTimeout(() => sendLogs(logs, retryCount + 1), 1000)
    }
  }
}

/**
 * Get user ID from localStorage if available
 */
function getUserId(): string | undefined {
  try {
    const authData = localStorage.getItem('firebase:authUser')
    if (authData) {
      const parsed = JSON.parse(authData)
      return parsed?.uid
    }
  } catch {
    // Silent fail
  }
  return undefined
}

/**
 * Flush on page unload using sendBeacon
 */
function setupUnloadHandler(): void {
  if (typeof window === 'undefined') return

  const handleUnload = () => {
    if (logBuffer.length === 0) return

    // sendBeacon is guaranteed to be sent even on page close
    try {
      const payload = JSON.stringify({ logs: logBuffer })
      navigator.sendBeacon(LOG_ENDPOINT, payload)
      logBuffer = []
    } catch {
      // Silent fail
    }
  }

  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      handleUnload()
    }
  })

  window.addEventListener('pagehide', handleUnload)
}

/**
 * Setup global error handler
 */
function setupGlobalErrorHandler(): void {
  if (typeof window === 'undefined') return

  // Catch unhandled errors
  window.addEventListener('error', (event) => {
    logError(
      'UNHANDLED_ERROR',
      event.message,
      `${event.filename}:${event.lineno}:${event.colno}`
    )
  })

  // Catch unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const message = event.reason instanceof Error
      ? event.reason.message
      : String(event.reason)
    logError('UNHANDLED_REJECTION', message)
  })
}

/**
 * Initialize error logger
 * Call this once at app startup
 */
export function initErrorLogger(): void {
  try {
    setupUnloadHandler()
    setupGlobalErrorHandler()
    console.log('[ErrorLogger] Initialized')
  } catch {
    // Silent fail
  }
}

// Auto-flush periodically
if (typeof window !== 'undefined') {
  setInterval(() => {
    if (logBuffer.length > 0) {
      flushLogs()
    }
  }, BATCH_INTERVAL_MS)
}
